// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    PILL_BORDER_RADIUS,
    PILL_HEIGHT,
    SankeyLayoutNode,
} from 'admin/failoverDomain/page/components/SummaryCards/useSankeyLayout';

const estimatePillWidth = (label: string) => Math.max(76, label.length * 8 + 28);

/**
 * Pill path: TL, TR, BR corners rounded; BL square so it attaches flush to the
 * node bar below it.
 */
const buildPillPath = (x: number, y: number, width: number): string => {
    const r = PILL_BORDER_RADIUS;
    const bottom = y + PILL_HEIGHT;
    return [
        `M ${x} ${bottom}`,
        `V ${y + r}`,
        `Q ${x} ${y} ${x + r} ${y}`,
        `H ${x + width - r}`,
        `Q ${x + width} ${y} ${x + width} ${y + r}`,
        `V ${bottom - r}`,
        `Q ${x + width} ${bottom} ${x + width - r} ${bottom}`,
        `H ${x}`,
        'Z',
    ].join(' ');
};

interface ISankeyPillProps {
    node: SankeyLayoutNode;
}

export const SankeyPill: React.FC<ISankeyPillProps> = ({ node }) => {
    const x = node.x0 ?? 0;
    const top = node.y0 ?? 0;
    const pillWidth = estimatePillWidth(node.label);

    return (
        <>
            <path d={buildPillPath(x, top - PILL_HEIGHT, pillWidth)} fill={node.color} />
            <text
                x={x + 12}
                y={top - PILL_HEIGHT / 2}
                fontSize={14}
                fontWeight="bold"
                fill="white"
                dominantBaseline="central"
            >
                {node.label}
            </text>
        </>
    );
};
